import type { KanhaChatConfig } from "./types";
import { resolveWorkerModuleUrl } from "./browser-retrieval";

declare const __KANHA_SPEECH_WORKER_URL__: string;

export const DEFAULT_SPEECH_MODEL = "onnx-community/moonshine-tiny-ONNX";
export const DEFAULT_SPEECH_MODEL_REVISION = "a6da1241cd305dcd64eab1edbd615f2bb9aabb95";
export const SPEECH_SAMPLE_RATE = 16000;
export const MAX_RECORDING_MS = 30000;
const MIN_SPEECH_SAMPLES = SPEECH_SAMPLE_RATE * 0.3;

export const MICROPHONE_BLOCKED_MESSAGE =
  "Microphone access was blocked. Allow it in your browser settings to use voice input.";
export const MICROPHONE_MISSING_MESSAGE = "No microphone was found on this device.";
export const MICROPHONE_FAILED_MESSAGE = "Voice input could not start. Please type your message instead.";
export const SPEECH_NOT_HEARD_MESSAGE = "Didn't catch that. Tap the mic and try again.";
export const TRANSCRIPTION_FAILED_MESSAGE =
  "Could not transcribe that. Please try again or type your message.";

export type VoiceInputState = "idle" | "recording" | "transcribing";

export interface VoiceInputStatus {
  state: VoiceInputState;
  loadProgress: number | null;
}

type SpeechConfig = Pick<KanhaChatConfig, "speechModel" | "speechWorkerUrl">;
export type SpeechWorkerRequest =
  | { type: "load"; model: string; revision: string }
  | { type: "transcribe"; model: string; revision: string; audio: Float32Array }
  | {
    type: "synthesize";
    model: string;
    revision: string;
    text: string;
    voice: string;
    language: string;
  };
export interface SynthesizedSpeech {
  audio: Float32Array<ArrayBuffer>;
  sampleRate: number;
}
type Pending = { resolve(value: unknown): void; reject(error: unknown): void };

let worker: Worker | null = null;
let workerModuleUrl: string | null = null;
let bootstrapUrl: string | null = null;
let nextId = 1;
const pending = new Map<number, Pending>();
const progressListeners = new Set<(model: string, progress: number) => void>();

export function onSpeechProgress(listener: (model: string, progress: number) => void) {
  progressListeners.add(listener);
  return () => {
    if (!progressListeners.delete(listener) || progressListeners.size > 0) return;
    worker?.terminate();
    worker = null;
    workerModuleUrl = null;
    revokeBootstrap();
    failPending(new Error("Speech worker was disposed"));
  };
}

export function voiceInputSupported() {
  return typeof navigator !== "undefined"
    && typeof navigator.mediaDevices?.getUserMedia === "function"
    && typeof MediaRecorder !== "undefined"
    && typeof OfflineAudioContext !== "undefined"
    && typeof Worker !== "undefined";
}

export function appendTranscript(current: string, transcript: string) {
  const existing = current.trimEnd();
  return existing ? `${existing} ${transcript}` : transcript;
}

export function speechModelIdentity(config: SpeechConfig) {
  const model = config.speechModel || DEFAULT_SPEECH_MODEL;
  return { model, revision: model === DEFAULT_SPEECH_MODEL ? DEFAULT_SPEECH_MODEL_REVISION : "main" };
}

function revokeBootstrap() {
  if (!bootstrapUrl) return;
  URL.revokeObjectURL(bootstrapUrl);
  bootstrapUrl = null;
}

function failPending(error: Error) {
  for (const request of pending.values()) request.reject(error);
  pending.clear();
}

function speechWorker(moduleUrl: string) {
  if (worker && workerModuleUrl === moduleUrl) return worker;
  if (worker) {
    worker.terminate();
    failPending(new Error("Speech worker was replaced"));
  }
  workerModuleUrl = moduleUrl;
  bootstrapUrl = URL.createObjectURL(new Blob(
    [`import ${JSON.stringify(moduleUrl)};`],
    { type: "text/javascript" },
  ));
  const created = new Worker(bootstrapUrl, { type: "module" });
  created.onmessage = (event: MessageEvent<{
    type: "progress" | "result" | "error";
    id?: number;
    model?: string;
    progress?: number;
    result?: unknown;
    message?: string;
  }>) => {
    revokeBootstrap();
    const data = event.data;
    if (data.type === "progress") {
      for (const listener of progressListeners) listener(data.model ?? "", data.progress ?? 0);
      return;
    }
    const request = pending.get(data.id ?? -1);
    if (!request) return;
    pending.delete(data.id!);
    if (data.type === "error") request.reject(new Error(data.message || "Speech worker failed"));
    else request.resolve(data.result);
  };
  created.onerror = (event) => {
    revokeBootstrap();
    failPending(new Error(event.message || "Speech worker failed to load"));
    created.terminate();
    if (worker === created) {
      worker = null;
      workerModuleUrl = null;
    }
  };
  worker = created;
  return created;
}

export function speechWorkerRequest<T>(message: SpeechWorkerRequest, moduleUrl: string) {
  const id = nextId;
  nextId += 1;
  return new Promise<T>((resolve, reject) => {
    const target = speechWorker(moduleUrl);
    pending.set(id, { resolve: resolve as (value: unknown) => void, reject });
    const transfer = message.type === "transcribe" ? [message.audio.buffer] : [];
    target.postMessage({ ...message, id }, transfer);
  });
}

export function mixToMono(buffer: Pick<AudioBuffer, "numberOfChannels" | "length" | "getChannelData">) {
  if (buffer.numberOfChannels === 1) return buffer.getChannelData(0).slice();
  const mono = new Float32Array(buffer.length);
  for (let channel = 0; channel < buffer.numberOfChannels; channel += 1) {
    const data = buffer.getChannelData(channel);
    for (let i = 0; i < mono.length; i += 1) mono[i] += data[i] / buffer.numberOfChannels;
  }
  return mono;
}

async function decodeSpeech(recording: Blob) {
  const context = new OfflineAudioContext(1, 1, SPEECH_SAMPLE_RATE);
  return mixToMono(await context.decodeAudioData(await recording.arrayBuffer()));
}

async function startRecording(onLimit: () => void) {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
  });
  const recorder = new MediaRecorder(stream);
  const chunks: Blob[] = [];
  recorder.ondataavailable = (event) => {
    if (event.data.size > 0) chunks.push(event.data);
  };
  const stopped = new Promise<Blob>((resolve) => {
    recorder.onstop = () => {
      for (const track of stream.getTracks()) track.stop();
      resolve(new Blob(chunks, { type: recorder.mimeType }));
    };
  });
  recorder.start();
  const limit = setTimeout(onLimit, MAX_RECORDING_MS);
  return () => {
    clearTimeout(limit);
    if (recorder.state !== "inactive") recorder.stop();
    return stopped;
  };
}

export function microphoneErrorMessage(error: unknown) {
  const name = error instanceof DOMException || error instanceof Error ? error.name : "";
  if (name === "NotAllowedError" || name === "SecurityError") return MICROPHONE_BLOCKED_MESSAGE;
  if (name === "NotFoundError" || name === "OverconstrainedError") return MICROPHONE_MISSING_MESSAGE;
  return MICROPHONE_FAILED_MESSAGE;
}

export interface VoiceInput {
  toggle(): void;
  cancel(): void;
  dispose(): void;
}

export function createVoiceInput(options: {
  config: () => SpeechConfig;
  onChange: (status: VoiceInputStatus) => void;
  onTranscript: (text: string) => void;
  onError: (message: string) => void;
}): VoiceInput {
  let status: VoiceInputStatus = { state: "idle", loadProgress: null };
  let stopRecording: (() => Promise<Blob>) | null = null;
  let session = 0;
  let target = { ...speechModelIdentity(options.config()), moduleUrl: "" };

  const update = (next: Partial<VoiceInputStatus>) => {
    status = { ...status, ...next };
    options.onChange(status);
  };
  const onProgress = (model: string, progress: number) => {
    if (model !== target.model) return;
    const loadProgress = progress < 1 ? Math.round(progress * 100) : null;
    if (loadProgress !== status.loadProgress) update({ loadProgress });
  };
  const unsubscribe = onSpeechProgress(onProgress);

  const start = async () => {
    const current = ++session;
    const config = options.config();
    target = {
      ...speechModelIdentity(config),
      moduleUrl: resolveWorkerModuleUrl(config.speechWorkerUrl, __KANHA_SPEECH_WORKER_URL__, "speechWorkerUrl"),
    };
    update({ state: "recording" });
    let stop: () => Promise<Blob>;
    try {
      stop = await startRecording(() => void finish());
    } catch (error) {
      if (current !== session) return;
      update({ state: "idle" });
      options.onError(microphoneErrorMessage(error));
      return;
    }
    if (current !== session) {
      void stop();
      return;
    }
    stopRecording = stop;
    const { model, revision, moduleUrl } = target;
    void speechWorkerRequest({ type: "load", model, revision }, moduleUrl).catch(() => {});
  };

  const finish = async () => {
    const stop = stopRecording;
    if (!stop) return;
    stopRecording = null;
    const current = session;
    const { model, revision, moduleUrl } = target;
    update({ state: "transcribing" });
    let message: string | null = null;
    try {
      const audio = await decodeSpeech(await stop());
      if (current !== session) return;
      const text = audio.length < MIN_SPEECH_SAMPLES
        ? ""
        : await speechWorkerRequest<string>({ type: "transcribe", model, revision, audio }, moduleUrl);
      if (current !== session) return;
      if (text) options.onTranscript(text);
      else message = SPEECH_NOT_HEARD_MESSAGE;
    } catch {
      message = TRANSCRIPTION_FAILED_MESSAGE;
    }
    if (current !== session) return;
    update({ state: "idle", loadProgress: null });
    if (message) options.onError(message);
  };

  const cancel = () => {
    session += 1;
    const stop = stopRecording;
    stopRecording = null;
    if (stop) void stop();
    if (status.state !== "idle") update({ state: "idle", loadProgress: null });
  };

  return {
    toggle() {
      if (status.state === "idle") void start();
      else if (status.state === "recording" && stopRecording) void finish();
      else if (status.state === "recording") cancel();
    },
    cancel,
    dispose() {
      cancel();
      unsubscribe();
    },
  };
}
